import { z } from 'zod';

/* Contratos de gestão de usuários do backoffice (Epic 28 / ADR-0348). Users vivem
   no auth-service; o admin agrega via HMAC (ADR-0222). A impersonation é o fluxo
   mais sensível: super_admin + reason≥10 + TTL 30min (licensing emite a license). */

export const listUsersQuery = z.object({
  search: z.string().min(2).max(120).optional(), // substring de email
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /* Filtro server-side de verificação de e-mail (ADR-0348): `verified` = só contas com
     `emailVerifiedAt` preenchido; `unverified` = só as sem. Ausente = todas. É server-side
     porque a lista não carrega a data de verificação de cada linha — o auth filtra por
     IS NULL/IS NOT NULL na origem (`emailVerifiedAt`). */
  verified: z.enum(['verified', 'unverified']).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuery>;

export const userAccessStatusDto = z.enum(['active', 'blocked']);
export type UserAccessStatus = z.infer<typeof userAccessStatusDto>;

export const userListItemDto = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().nullable(),
  /* E-mail verificado (ADR-0348): boolean derivado de `emailVerifiedAt` na origem — a lista
     não expõe a data, só o estado, o suficiente para o badge e o filtro server-side. */
  emailVerified: z.boolean(),
  status: userAccessStatusDto,
  createdAt: z.string(),
});
export type UserListItem = z.infer<typeof userListItemDto>;

/* Criação de conta comum pelo backoffice (ADR-0710): o app é passwordless (não há
   signup por senha) — hoje uma conta só nasce implicitamente no 1º login (OTP/OAuth).
   O staff cria uma conta PRÉ-VERIFICADA (sem senha); o usuário faz login depois via
   OTP. A resposta reusa `userListItemDto` (id/email/name/createdAt). */
export const createUserBody = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120).optional(),
});
export type CreateUserBody = z.infer<typeof createUserBody>;

export const userOrgMembershipDto = z.object({
  orgId: z.string(),
  orgName: z.string(),
  role: z.string(),
  joinedAt: z.string(),
});

export const userAuditEntryDto = z.object({
  action: z.string(),
  actorId: z.string(),
  /* Email do operador via LEFT JOIN em `staff_users` (ADR-0708); `null` p/ ator órfão. */
  actorEmail: z.string().nullable(),
  /* Justificativa livre da ação, extraída da `metadata` JSONB do audit log (ADR-0708);
     `null` quando a ação não gravou um `reason`. */
  reason: z.string().nullable(),
  ts: z.string(),
});

/* Dimensões do detalhe agregadas do licensing-service via HMAC (ADR-0356/0853):
   licenses (entitlements), devices (heartbeats + cooldown) e sessions. A
   disponibilidade viaja separada dos itens: `[]` só significa zero quando a
   dimensão foi realmente lida; falha de upstream nunca se disfarça de vazio. */
export const userLicenseDto = z.object({
  packId: z.string(),
  version: z.string(),
  acquiredAt: z.string(),
  expiresAt: z.string().nullable(),
  kind: z.string().nullable(),
});

export const userDeviceDto = z.object({
  deviceId: z.string(),
  appVersion: z.string(),
  lastSeenAt: z.string(),
  suspect: z.boolean(),
  blockedUntil: z.string().nullable(),
});

export const userSessionDto = z.object({
  id: z.string(),
  deviceId: z.string(),
  deviceName: z.string().nullable(),
  orgId: z.string().nullable(),
  createdAt: z.string(),
  lastUsedAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
});

export const userDimensionAvailabilityDto = z.enum(['available', 'unavailable']);
export const userLicensingAvailabilityDto = z.object({
  licenses: userDimensionAvailabilityDto,
  devices: userDimensionAvailabilityDto,
  sessions: userDimensionAvailabilityDto,
});

/* Detail completo (ADR-0356): identidade completa + orgs (auth) + licenses/devices/
   sessions (licensing) + auditoria recente (DB local). Identidade estendida (ADR-0648/
   0221): `username` (handle público notNull), `avatarUrl` (nullable), `emailVerifiedAt`
   (nullable — null = não verificado) e `updatedAt` — tudo já na row de `users`. */
export const userDetailDto = userListItemDto.extend({
  username: z.string(),
  avatarUrl: z.string().nullable(),
  emailVerifiedAt: z.string().nullable(),
  updatedAt: z.string(),
  blockedAt: z.string().nullable(),
  blockedReason: z.string().nullable(),
  /* Providers OAuth vinculados (ADR-0145): `['github','google']` etc. Exige um JOIN em
     `oauth_accounts` no auth (fora da row de `users`); vazio quando a conta só tem OTP. */
  oauthProviders: z.array(z.string()),
  orgs: z.array(userOrgMembershipDto),
  licenses: z.array(userLicenseDto),
  devices: z.array(userDeviceDto),
  sessions: z.array(userSessionDto),
  licensingAvailability: userLicensingAvailabilityDto,
  recentAudit: z.array(userAuditEntryDto),
});
export type UserDetail = z.infer<typeof userDetailDto>;

/* `reason` ≥10 chars: justificativa obrigatória da impersonation (ADR-0348). */
export const impersonateStartBody = z.object({ reason: z.string().min(10).max(500) });
export type ImpersonateStartBody = z.infer<typeof impersonateStartBody>;

export const impersonateStartResponse = z.object({
  token: z.string(),
  sessionId: z.string(),
  expiresAt: z.string(),
  userEmail: z.string().email(),
});
export type ImpersonateStartResponse = z.infer<typeof impersonateStartResponse>;

export const impersonateEndBody = z.object({ sessionId: z.string().min(1) });
export type ImpersonateEndBody = z.infer<typeof impersonateEndBody>;

/* Revogação de sessão pelo operador (suporte/super_admin): `reason` ≥10 (ADR-0356). */
export const revokeSessionBody = z.object({ reason: z.string().min(10).max(500) });
export type RevokeSessionBody = z.infer<typeof revokeSessionBody>;

/* Bloqueio/desbloqueio de conta (ADR-0853): ação de alto risco, sempre com
   justificativa substantiva. O mesmo body vale para as duas transições. */
export const changeUserAccessBody = z.object({ reason: z.string().min(20).max(500) });
export type ChangeUserAccessBody = z.infer<typeof changeUserAccessBody>;

export const userAccessEnforcementLimitationDto = z.enum([
  'existing_access_tokens_expire_naturally',
  'licensing_sessions_not_revoked',
]);
export const userAccessMutationResponseDto = z.object({
  status: userAccessStatusDto,
  blockedAt: z.string().nullable(),
  blockedReason: z.string().nullable(),
  alreadyInState: z.boolean(),
  revokedAuthSessions: z.number().int().nonnegative(),
  enforcementLimitations: z.array(userAccessEnforcementLimitationDto),
});
export type UserAccessMutationResponse = z.infer<typeof userAccessMutationResponseDto>;
